import React, {useCallback, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {IconArrowLeft, IconArrowRight} from '@flixbus/honeycomb-icons-react';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {getPartners} from '@/Pages/Partners/store/thunks';
import {getProductionCostBuses} from '@/Pages/ProductionCost/store/thunks';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {CalculationUserJourneyMode} from '@/Pages/ProductionCost/store/types';
import {
  backToFirstDataImportScreen,
  resetProductionCostState
} from '@/Pages/ProductionCost/store/slice';
import {resetDataTableEntries} from '@/Pages/Home/store/slice';
import {ProductionCostCalculationPath} from '@/Pages/ProductionCost/constants/routePaths';
import ProductionCostHeader from '../ProductionCostHeader';
import ProductionCostFooter from '../ProductionCostFooter';
import {ProductionCostHeaderStepOption} from '../ProductionCostHeader/ProductionCostHeaderSteps/ProductionCostHeaderSteps.types';
import {ProductionCostFooterButton} from '../ProductionCostFooter/ProductionCostFooter.types';
import useCalculateProductionCost from './hooks/useCalculateProductionCost';
import ProductionCostErrorNotification from './ProductionCostErrorNotification';
import ProductionCostKpisList from './ProductionCostKpisList';
import CalculationLogicSettings from './CalculationLogicSettings';
import Lines from './Lines';
import InvalidCostFactorsPopup from './InvalidCostFactorsPopup';
import LineVehicles from './LineVehicles';
import styles from './index.module.scss';

const ProductionCostStepTwo: React.FC = () => {
  const dispatch = useAppDispatch();
  const productionCostEntryId = useAppSelector(
    ({productionCost}) => productionCost.id
  );
  const partners = useAppSelector(({partners}) => partners.list);
  const vehicleTypes = useAppSelector(({vehicleTypes}) => vehicleTypes.list);
  const calculationUserJourneyMode = useAppSelector(
    ({productionCost}) => productionCost.calculationUserJourneyMode
  );
  const loading = useAppSelector(({productionCost}) => productionCost.loading);

  const formIsInvalid = useAppSelector(
    ({productionCost}) =>
      Object.keys(productionCost.validationErrors.busValues).length > 0 ||
      Object.keys(productionCost.validationErrors.busScheduleValues).length > 0
  );

  // Redirects to the Home page if productionCostId is not provided
  // Happens when user refreshes the page
  const navigate = useNavigate();
  useEffect(() => {
    if (!productionCostEntryId) navigate('/');
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!partners) dispatch(getPartners());
    if (productionCostEntryId)
      dispatch(getProductionCostBuses(productionCostEntryId));
  }, [dispatch, partners, vehicleTypes, productionCostEntryId]);

  const goToSummary = useCallback(
    () => navigate(`../${ProductionCostCalculationPath.steps.summary}`),
    [navigate]
  );
  const calculateProductionCost = useCalculateProductionCost(goToSummary);

  const backButton: ProductionCostFooterButton = useMemo(
    () => ({
      label: 'Back',
      leftSideIcon: IconArrowLeft,
      onClick: () => {
        if (
          calculationUserJourneyMode ===
          CalculationUserJourneyMode.EditCalculation
        ) {
          dispatch(resetProductionCostState());
          dispatch(resetDataTableEntries());
          navigate(`/`);
        } else {
          dispatch(backToFirstDataImportScreen());
          navigate(`../${ProductionCostCalculationPath.steps.selectSchedules}`);
        }
      }
    }),
    [calculationUserJourneyMode, dispatch, navigate]
  );

  const nextButton: ProductionCostFooterButton = useMemo(() => {
    const rightSideIcon = IconArrowRight;
    const appearance = 'primary';
    const onClick = calculateProductionCost;
    const disabled = formIsInvalid || loading;

    return calculationUserJourneyMode ===
      CalculationUserJourneyMode.EditCalculation
      ? {
          rightSideIcon,
          appearance,
          onClick,
          disabled,
          label: 'Calculate & Save',
          dataActionName: 'Data entry Save button'
        }
      : {
          rightSideIcon,
          appearance,
          onClick,
          disabled,
          label: 'Next',
          dataActionName: 'Data entry Next button'
        };
  }, [
    calculationUserJourneyMode,
    formIsInvalid,
    loading,
    calculateProductionCost
  ]);

  return (
    <>
      <div className={styles.header}>
        <ProductionCostHeader
          mode={calculationUserJourneyMode}
          step={ProductionCostHeaderStepOption.DataEntry}
        />
      </div>

      <div className={styles.body}>
        <ProductionCostErrorNotification />
        <ProductionCostKpisList />
        <LineVehicles />
        <CalculationLogicSettings />
        <Lines />
      </div>

      <ProductionCostFooter
        leftSideButtons={[backButton]}
        rightSideButtons={[nextButton]}
      />

      <InvalidCostFactorsPopup />
    </>
  );
};

export default React.memo(ProductionCostStepTwo);
